// useSellSubscription — TanStack Query хуки для продажи абонемента из карточки клиента.
// Своя прослойка под features/clients (НЕ трогаем features/subscriptions/*).
// Query keys включают tenantId (см. useClients.ts). По успеху продажи инвалидируем
// корень ['clients'] (счётчик activeSubscriptionsCount + списки) и корень ['subscriptions'].

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useTenantId } from '@/lib/tenant-context';

import {
  createCashSale,
  listOpenShift,
  listSubscriptionTemplates,
  sellSubscription,
  type CashSaleResult,
  type CreateCashSaleBody,
  type EntitlementInput,
  type SellSubscriptionBody,
  type SubscriptionDetail,
  type SubscriptionTemplate,
} from '../api/subscriptionActions.api';

export function subscriptionActionsKeys(tenantId: string | null) {
  return {
    templates: ['subscriptions', tenantId, 'templates'] as const,
    openShift: ['subscriptions', tenantId, 'open-shift'] as const,
  };
}

export function useSubscriptionTemplates(
  /**
   * Филиал продажи. Филиал ОБЯЗАН быть в ключе кэша: иначе выборка одного
   * филиала досталась бы другому — тот же прайс под тем же ключом.
   */
  branchId?: string,
): UseQueryResult<SubscriptionTemplate[], Error> {
  const tenantId = useTenantId();
  const keys = subscriptionActionsKeys(tenantId);

  return useQuery({
    queryKey: [...keys.templates, branchId ?? 'all'],
    queryFn: async () => (await listSubscriptionTemplates(branchId)).data,
    staleTime: 60_000, // шаблоны меняются редко.
  });
}

/**
 * Строит минимально валидный payload entitlements из выбранного шаблона.
 *
 * Допущение (template→entitlements mapping в DTO неоднозначен):
 *  - Если у шаблона есть group_ids — создаём по одному entitlement на каждую группу
 *    (server валидирует group_id ∈ template.groups).
 *  - Если групп нет — один entitlement без группы (group_id: null) — простой абонемент.
 *  - sessions_total берём из template.sessions_count (null = безлимит/unlimited_period).
 *  - priority оставляем дефолтным (server подставит 100), поле опускаем.
 * Это даёт массив длиной ≥ 1 (CreateSubscriptionSchema требует .min(1)).
 */
export function buildEntitlementsFromTemplate(
  template: Pick<SubscriptionTemplate, 'group_ids' | 'sessions_count'>,
): EntitlementInput[] {
  const sessionsTotal = template.sessions_count;
  if (template.group_ids.length > 0) {
    return template.group_ids.map((groupId) => ({
      group_id: groupId,
      sessions_total: sessionsTotal,
    }));
  }
  return [{ group_id: null, sessions_total: sessionsTotal }];
}

export function useSellSubscription(): UseMutationResult<
  SubscriptionDetail,
  Error,
  SellSubscriptionBody
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SellSubscriptionBody) => sellSubscription(body),
    onSuccess: () => {
      // Карточка клиента (activeSubscriptionsCount) + любые списки клиентов.
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Списки абонементов под этим тенантом.
      void queryClient.invalidateQueries({ queryKey: ['subscriptions', tenantId] });
    },
  });
}

/**
 * Открытая кассовая смена ({ id, branch_id } | null). Нужна, чтобы принять оплату.
 * Сверку branch_id с филиалом продажи делает сама модалка.
 */
export function useOpenShift(
  opts: { branchId?: string | null; enabled?: boolean } = {},
): UseQueryResult<{ id: string; branch_id: string } | null, Error> {
  const tenantId = useTenantId();
  const keys = subscriptionActionsKeys(tenantId);
  const branchId = opts.branchId ?? undefined;

  return useQuery({
    queryKey: [...keys.openShift, branchId ?? 'any'],
    queryFn: () => listOpenShift(branchId),
    staleTime: 30_000, // смена меняется нечасто, но реже шаблонов — обновляем чаще.
    // `enabled` — не косметика: GET /cash/shifts закрыт правом
    // `reports:branch_finance`, которого нет у педагога. Безусловный хук дал бы
    // ему 403 на каждой странице (RbacGuard fail-closed). Гейт именно здесь,
    // а не ранним `return` у вызывающего: хуки — до первого return.
    enabled: opts.enabled ?? true,
  });
}

/**
 * Продажа абонемента через кассу (Sale + платежи). По успеху инвалидируем
 * карточку клиента и списки абонементов под текущим тенантом.
 */
export function useCreateCashSale(): UseMutationResult<CashSaleResult, Error, CreateCashSaleBody> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateCashSaleBody) => createCashSale(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['subscriptions', tenantId] });
    },
  });
}
